<script setup lang="ts">
/**
 * Карточка категории для двухуровневого каталога.
 *
 * Чисто презентационная: получает категорию и настройки лейбла, отдаёт клик.
 * Благодаря этому её же рисует превью в разделе «Внешний вид».
 *
 * Скругление берётся из общего пресета «Скругления элементов», а не из отдельной
 * настройки лейбла: два независимых радиуса в одном интерфейсе быстро расходятся,
 * и карточка категории начинает жить своей формой в отрыве от остальной витрины.
 */
import { computed } from 'vue';
import type { CategoryLabelSurface } from '@shared/config';

const props = withDefaults(
  defineProps<{
    /** Категория из каталога: { category, to, img, products }. */
    category: any;
    surface?: CategoryLabelSurface;
    /** Непрозрачность подложки лейбла, проценты. */
    opacity?: number;
    /** Показывать «12 блюд» под названием. */
    showCount?: boolean;
    /**
     * Адрес категории. Задан — плитка становится настоящей ссылкой, и категории
     * появляются во внутренней перелинковке (робот по <button> не ходит).
     * Не задан — остаётся кнопкой: в превью админки ссылка никуда не ведёт.
     */
    href?: string;
  }>(),
  { surface: 'dark', opacity: 55, showCount: true, href: '' },
);

const emit = defineEmits<{ (event: 'select'): void }>();

/** Переход остаётся клиентским: href нужен роботу и «открыть в новой вкладке». */
const onClick = (event: MouseEvent) => {
  if (!props.href) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  event.preventDefault();
  emit('select');
};

const imageUrl = computed(() => props.category?.img?.url || '');
const productCount = computed(() =>
  Array.isArray(props.category?.products) ? props.category.products.length : 0,
);

/** Склонение к числу позиций: «1 блюдо», «2 блюда», «5 блюд». */
const countLabel = computed(() => {
  const n = productCount.value;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} блюдо`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} блюда`;
  return `${n} блюд`;
});

const labelStyle = computed(() => {
  if (props.surface === 'none') return;
  const alpha = props.opacity / 100;
  return props.surface === 'dark'
    ? { background: `rgb(0 0 0 / ${alpha})` }
    : { background: `rgb(255 255 255 / ${alpha})` };
});
</script>

<template>
  <component
    :is="href ? 'a' : 'button'"
    :type="href ? undefined : 'button'"
    :href="href || undefined"
    class="category-tile"
    :class="`category-tile--${surface}`"
    @click="href ? onClick($event) : emit('select')"
  >
    <img v-if="imageUrl" class="category-tile__img" :src="imageUrl" :alt="category.category" loading="lazy" />
    <div v-else class="category-tile__img category-tile__img--empty" />

    <div class="category-tile__label" :style="labelStyle">
      <span class="category-tile__name brand-font">{{ category.category }}</span>
      <span v-if="showCount && productCount > 0" class="category-tile__count">{{ countLabel }}</span>
    </div>
  </component>
</template>

<style scoped lang="scss">
.category-tile {
  @apply relative block w-full overflow-hidden text-left isolate;
  border-radius: var(--app-radius-card, 24px);
  // Обводка из настройки «Обводка карточек и кнопок» — плитка категории тоже карточка
  border: var(--app-border-width, 0) solid var(--app-border-color, transparent);
  aspect-ratio: 4 / 3;
  // Фон нужен, пока картинка грузится и когда её у категории нет вовсе
  background: linear-gradient(135deg, var(--app-accent, #ef4444) 0%, rgb(0 0 0 / 0.85) 100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &__img {
    @apply absolute inset-0 w-full h-full object-cover object-center;

    &--empty {
      @apply bg-transparent;
    }
  }

  &__label {
    @apply relative z-[2] m-2 md:m-3 px-3 py-2 flex flex-col gap-0.5;
    // Лейбл прижат к низу карточки
    @apply mt-auto;
    border-radius: var(--app-radius-control, 9999px);
    backdrop-filter: blur(6px);
  }

  // Без подложки текст ложится прямо на фото — спасаем читаемость тенью
  &--none .category-tile__label {
    @apply px-0 py-0;
    backdrop-filter: none;

    .category-tile__name,
    .category-tile__count {
      text-shadow: 0 1px 8px rgb(0 0 0 / 0.75);
    }
  }

  &--light .category-tile__name {
    @apply text-gray-900;
  }

  &--light .category-tile__count {
    @apply text-gray-700;
  }

  &__name {
    @apply text-base md:text-lg font-bold leading-tight text-white;
  }

  &__count {
    @apply text-xs text-white/75;
  }

  // Контент выравниваем вниз: label прижат mt-auto
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

@media (hover: hover) and (pointer: fine) {
  .category-tile:hover {
    transform: translateY(-3px);
    box-shadow: 0 18px 28px -12px rgb(0 0 0 / 0.35);
  }
}

@media (prefers-reduced-motion: reduce) {
  .category-tile {
    transition: none;
  }
}
</style>
